import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Alert from 'components/Alert/Alert';

import { selectActiveApplicationDetails } from 'features/applications/applicationsSelectors';
import { setApplicationId as setInterviewSlotsApplicationId } from 'features/interviewSlots/interviewSlotsSlice';
import { selectAppLocale } from 'features/locale/localeSelectors';

import { useInterviewState } from 'hooks/useInterviewState';
import usePageviewEvent from 'hooks/usePageViewEvent';

import {
  AlertType,
  ApplicationStage,
  GTMEvents,
  InterviewStep
} from 'constants/enums';
import { TOUR_STEP_TARGET_APPLICATION_INTERVIEW_TAB } from 'constants/tour';

import styles from './InterviewView.module.scss';
import AwaitingResponse from './components/Views/AwaitingResponse/AwaitingResponse';
import ConfirmInterview from './components/Views/ConfirmInterview/ConfirmInterview';
import InterviewCanceled from './components/Views/InterviewCanceled/InterviewCanceled';
import InterviewDeclined from './components/Views/InterviewDeclined/InterviewDeclined';
import InterviewScheduled from './components/Views/InterviewScheduled/InterviewScheduled';
import RequestInterview from './components/Views/RequestInterview/RequestInterview';
import LoadingState from './components/Views/components/LoadingState/LoadingState';

interface InterviewViewProps {
  applicationId: string | null;
  fetchApplicationData: (
    applicationId: string,
    locale: string
  ) => Promise<void>;
  // TODO: remove once sidebar uses container-query layout instead of prop override
  isSidebarMode?: boolean;
}

const InterviewView: FC<InterviewViewProps> = ({
  applicationId,
  fetchApplicationData,
  isSidebarMode = false
}: InterviewViewProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const application = useSelector(selectActiveApplicationDetails);
  const locale = useSelector(selectAppLocale);

  const syncedApplication =
    applicationId != null && application?.id === applicationId
      ? application
      : undefined;

  const {
    isLoading,
    setIsLoading,
    interviewRequest,
    interviewHistoryLoaded,
    fetchInterviewRequest
  } = useInterviewState(applicationId);

  usePageviewEvent(GTMEvents.ApplicationsListViewSidebarInterview);

  const tourStepId = TOUR_STEP_TARGET_APPLICATION_INTERVIEW_TAB;

  const isHired =
    syncedApplication?.stage === ApplicationStage.HiredApplicationStage;

  const isDeclined =
    syncedApplication?.stage === ApplicationStage.DeclinedApplicationStage;

  const refreshInterviewAndApplication = async () => {
    await fetchInterviewRequest();
    if (applicationId) {
      await fetchApplicationData(applicationId, locale);
    }
  };

  useEffect(() => {
    dispatch(setInterviewSlotsApplicationId(applicationId));
  }, [applicationId, dispatch]);

  const renderContent = () => {
    if (isDeclined) {
      return (
        <div id={tourStepId}>
          <InterviewDeclined />
        </div>
      );
    }

    if (isHired) {
      return (
        <div>
          <div className={styles.title}>
            {t('sidebar.application.interview.unavailable')}
          </div>
          <Alert type={AlertType.Info}>
            {t('sidebar.application.interview.hired')}
          </Alert>
        </div>
      );
    }

    if (!interviewHistoryLoaded) {
      return (
        <div id={tourStepId}>
          <LoadingState
            title={t('sidebar.application.interview.interviewProcess')}
          />
        </div>
      );
    }

    if (!interviewRequest) {
      return (
        <div id={tourStepId}>
          <RequestInterview
            isLoading={isLoading}
            onLoading={setIsLoading}
            onRequestSent={refreshInterviewAndApplication}
          />
        </div>
      );
    }

    switch (interviewRequest.status) {
      case InterviewStep.InterviewRequestSent:
        return (
          <div id={tourStepId}>
            <AwaitingResponse
              isLoading={isLoading}
              onLoading={setIsLoading}
              interviewRequest={interviewRequest}
              onActionCompleted={fetchInterviewRequest}
            />
          </div>
        );

      case InterviewStep.SlotProvided:
        return (
          <div id={tourStepId}>
            <ConfirmInterview
              interviewRequest={interviewRequest}
              onConfirmed={refreshInterviewAndApplication}
              onCanceled={refreshInterviewAndApplication}
              isLoading={isLoading}
              onLoading={setIsLoading}
              application={syncedApplication ?? null}
              isSidebarMode={isSidebarMode}
            />
          </div>
        );

      case InterviewStep.Scheduled:
        return (
          <div id={tourStepId}>
            <InterviewScheduled interviewDetails={interviewRequest} />
          </div>
        );

      case InterviewStep.Withdrawn:
        return (
          <div id={tourStepId}>
            <InterviewCanceled />
          </div>
        );

      case InterviewStep.Declined:
        return (
          <div id={tourStepId}>
            <InterviewDeclined />
          </div>
        );

      default:
        return (
          <div id={tourStepId}>
            <LoadingState
              title={t('sidebar.application.interview.interviewProcess')}
            />
          </div>
        );
    }
  };

  return <>{renderContent()}</>;
};

export default InterviewView;
